import React, { useCallback, useMemo, useState } from "react";
import { useTranslationV3 } from "../../i18nV3/useTranslationV3";
import { Title, Text, SmallText } from "./components/Typography";
import { CancellationProposal } from "../../types/Cancellation";
import FlightBounds from "./components/FlightBounds";
import { getBoundsFromCancellationProposal } from "../../services/cancellationProposalService";
import styled from "styled-components";
import { CancellationRefundType } from "../../enums/Cancellation";
import { Breakpoint } from "../../enums/Breakpoints";
import { formatPrice } from "../../utils/priceUtils";
import { useConfig } from "../../contexts";
import BaseButton from "../../components/TixUiButton";
import Modal from "../Modal";
import { confirmCancellationProposal } from "../../api/cancellation";
import ErrorMessage from "../ErrorMessage";

const LABEL_PER_REFUND_TYPE = {
  [CancellationRefundType.FullRefund]:
    "lp_cancellation_request_content_full_refund",
  [CancellationRefundType.RefundWithPenalty]:
    "lp_cancellation_request_content_partial_refund",
  [CancellationRefundType.NonRefundable]:
    "lp_cancellation_request_content_no_refund",
};

interface Props {
  cancellationProposal: CancellationProposal;
}

const RequestView = ({ cancellationProposal }: Props) => {
  const { t } = useTranslationV3();
  const config = useConfig();

  const bounds = useMemo(
    () => getBoundsFromCancellationProposal(cancellationProposal),
    [cancellationProposal]
  );

  const [hasError, setHasError] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const handleConfirmClick = useCallback(async () => {
    setHasError(false);
    try {
      setIsSubmitting(true);
      await confirmCancellationProposal(cancellationProposal.offerUuid);
      setIsConfirmed(true);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsConfirmModalOpen(false);
      setIsSubmitting(false);
    }
  }, [cancellationProposal.offerUuid]);

  const contentForRefundType = useMemo(() => {
    if (!cancellationProposal.refundType || !bounds[0]) return;
    const labelKey = LABEL_PER_REFUND_TYPE[cancellationProposal.refundType];
    const bindings = {
      amount: formatPrice(config.defaultLang, {
        amount: cancellationProposal.refundInfo!.refundAmount!,
        currency: cancellationProposal.refundInfo!.currency,
      }),
      platingCarrier: bounds[0].platingCarrier,
      penalty: formatPrice(config.defaultLang, {
        amount: cancellationProposal.refundInfo!.penaltyPerTicket!,
        currency: cancellationProposal.refundInfo!.currency,
      }),
    };
    return t(labelKey, bindings);
  }, [bounds, cancellationProposal, config.defaultLang, t]);

  return (
    <>
      {hasError && (
        <StyledErrorMessage error={t("lp_cancellation_request_submit_error")} />
      )}
      {isConfirmed ? (
        <>
          <Title>
            {t("lp_cancellation_request_confirmed_title", {
              tripId: cancellationProposal.tripId,
            })}
          </Title>
          <Paragraph>
            {t("lp_cancellation_request_confirmed_thanks", {
              destination: bounds[0]?.destination.city,
              platingCarrier: bounds[0]?.platingCarrier,
            })}
          </Paragraph>
          <Subtitle>
            {t("lp_cancellation_request_confirmed_contact_info")}
          </Subtitle>
        </>
      ) : (
        <>
          <Title>
            {t("lp_cancellation_request_title", {
              tripId: cancellationProposal.tripId,
            })}
          </Title>
          <Subtitle>
            {t("lp_cancellation_request_subtitle", {
              destination: bounds[0]?.destination.city,
              platingCarrier: bounds[0]?.platingCarrier,
            })}
          </Subtitle>
        </>
      )}
      <TripIdIndicator>
        {t("lp_cancellation_trip_id", { tripId: cancellationProposal.tripId })}
      </TripIdIndicator>
      <StyledFlightBounds bounds={bounds} isCancelled={isConfirmed} />
      {!isConfirmed && (
        <>
          <Paragraph>{contentForRefundType}</Paragraph>
          <Paragraph>
            {t("lp_cancellation_request_content_final_warning")}
          </Paragraph>
          <SubmitButton onClick={() => setIsConfirmModalOpen(true)}>
            {t("lp_cancellation_request_submit_button")}
          </SubmitButton>
        </>
      )}
      <Modal
        isOpened={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        header={t("lp_cancellation_request_confirm_modal_title")}
      >
        <Paragraph>
          {t("lp_cancellation_request_confirm_modal_content")}
        </Paragraph>
        <Button onClick={handleConfirmClick} isLoading={isSubmitting}>
          {t("lp_cancellation_request_confirm_modal_submit_button")}
        </Button>
      </Modal>
    </>
  );
};

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 20px;
`;

const Subtitle = styled(Text)`
  margin-bottom: 22px;
`;

const TripIdIndicator = styled(SmallText)`
  text-align: right;
  margin-bottom: 10px;
`;

const Paragraph = styled(Text)`
  margin-bottom: 20px;
`;

const StyledFlightBounds = styled(FlightBounds)`
  margin-bottom: 40px;
`;

// Buttons
const Button = styled(BaseButton)`
  background: ${({ theme }) => theme.redesign.errorColor};
  width: auto;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.redesign.errorColor};
  }
`;

const SubmitButton = styled(Button)`
  @media ${Breakpoint.MinTablet} {
    margin-top: 40px;
  }
`;

export default RequestView;
